import { redactUrl } from './url-redaction';

/**
 * Reduces a request URL to path + query, so an `ApiError` tags the same shape
 * the axios branch does.
 *
 * `enrichAxiosError` passes `config.url`, which is relative, and `redactUrl`
 * keeps the origin of an absolute one. `ApiError.url` is absolute, so without
 * this the two transports would emit `http.url` values that never group
 * together — the exact indistinguishability this enrichment exists to remove.
 * The query survives to be filtered by `redactUrl`'s allowlist rather than
 * dropped here, which keeps the booking shape that makes an error reproducible.
 *
 * Returns the input untouched when it is not absolute, and `undefined` when it
 * cannot be parsed at all.
 */
const toRequestPath = (url: string): string | undefined => {
  try {
    const { pathname, search } = new URL(url);
    return `${pathname}${search}`;
  } catch {
    return url.startsWith('/') ? url : undefined;
  }
};

/**
 * Derives HTTP attributes from an `ApiError` thrown by the typed client.
 *
 * The sibling of `enrichAxiosError`, and deliberately the same shape: while the
 * openapi-fetch migration is in flight both transports produce errors, and a
 * migrated endpoint's record has to stay as diagnosable as an axios one.
 * Without `http.url` a 500 from `configuration` and one from `last-booking` are
 * indistinguishable — a gap that grows with every migrated endpoint.
 *
 * Matched on `name` rather than `instanceof ApiError` on purpose: importing
 * `shared/api/api-client` here would point the observability module, which is
 * imported first thing at boot, at the API layer.
 *
 * `http.method` is absent because `ApiError` cannot carry it — `Response` does
 * not expose the request method. The URL makes it redundant in practice.
 *
 * Returns `{}` for anything that is not an `ApiError`.
 */
export const enrichApiError = (error: unknown): Record<string, unknown> => {
  if (!(error instanceof Error) || error.name !== 'ApiError') return {};

  const { status, url } = error as Error & { status?: number; url?: string };
  const path = url && toRequestPath(url);

  return {
    'http.url': (path && redactUrl(path)) || undefined,
    'http.status_code': String(status ?? 'unknown'),
  };
};
